import * as fabric from "fabric";
import React, { useEffect, useRef, useState } from "react";
import { LayoutElement } from "../@types/data";
import { CustomFabricObject } from "../@types/global";
import { useModal } from "../Context/ModalContext";
// @ts-ignore
import { useAppDispatch, useAppSelector } from "../hook/redux";
import store from "../store";
import { backgroundCreation } from "../utils/backgroundCreation";
import { createCanvasImage, createCanvasImageExisting, recreateImage } from "./Canvas/createCanvasImage";
import { createCanvasSignature } from "./Canvas/createCanvasSignature";
import { createAlertRect, createCanvasTextBox, createCanvasTextBoxExisting } from "./Canvas/createCanvasText";
import { checkIfInsideClipRect } from "./Canvas/imagesUtils";
import { renderModal } from "./Modal/ModalUtils";
import {
  checkCanvasBounds,
  clearCanvas,
  COMPLETE_SIZE_BASE,
  handleClickOutsideCanvas,
  handleMouseOut,
  handleMouseOver,
  handleResize,
  handleTouchEnd,
  handleTouchMove,
  handleTouchStart,
  removeDuplicateObjects
} from "./templateUtils";
import { handleDisplayToolbar } from "./utils";
import { placeholderBase64 } from "../data/placeholder";


export const initializeCanvasWithLayout = (
  layout: LayoutElement,
  fabricCanvasInstanceRef: { current: fabric.Canvas | null },
  scaledWidth: number,
  scaledHeight: number,
  initializeCanvasChild: ( layout: LayoutElement ) => Promise<void>
) => {
  if ( layout && fabricCanvasInstanceRef.current && scaledWidth > 0 && scaledHeight > 0 ) {

    initializeCanvasChild( layout );

    setTimeout( () => {
      fabricCanvasInstanceRef.current?.requestRenderAll();
    }, 150 );
  }
};

export default function Template( { fabricCanvasInstanceRef } ) {
  const {
    selectedBackground,
    currentData,
    scale,
    alert,
    qrCode,
    selectedLayout,
    layouts,
    toolbarDisplay,
    signatures,
    existingData,
    textDatas
  } = useAppSelector( ( state ) => state.editor );
  const orientation = useAppSelector(
    ( state ) => state.editor.currentData.paramGeneral.orient
  );

  const [ scaledWidth, setScaledWidth ] = useState<number>( 0 );
  const [ scaledHeight, setScaledHeight ] = useState<number>( 0 );
  const [ scaledDimensions, setScaledDimensions ] = useState( { width: 0, height: 0, layout: selectedLayout } );
  const [ layout, setLayout ] = useState(
    layouts.find( ( layout ) => layout.id === selectedLayout )
  );

  const canvasElementRef = useRef<HTMLCanvasElement | null>( null );
  const toolbarRef = useRef<HTMLDivElement | null>( null );

  const isQrCodeEnabled = currentData.paramGeneral.isQRCode;

  const dispatch = useAppDispatch();
  const { type } = useModal();

  useEffect( () => {
    (async () => {
      await backgroundCreation( selectedBackground, orientation, fabricCanvasInstanceRef.current, scale );
    })();
  }, [ selectedBackground, fabricCanvasInstanceRef.current, scale, orientation ] );

  useEffect( () => {
    document.addEventListener("mousedown", (event) => handleClickOutsideCanvas(event, fabricCanvasInstanceRef.current, canvasElementRef.current, toolbarRef.current))

    return () => {
      document.removeEventListener( "mousedown",
        ( event ) => handleClickOutsideCanvas( event,
          fabricCanvasInstanceRef.current,
          canvasElementRef.current,
          toolbarRef.current ) );
    };
  }, [ fabricCanvasInstanceRef, canvasElementRef ] );

  useEffect( () => {
    window.addEventListener( "resize", () => {
      handleResize( dispatch, orientation )} );

    handleResize( dispatch, orientation );
    return () => window.removeEventListener( "resize", () => handleResize( dispatch, orientation ) );
  }, [ dispatch, orientation ] );

  useEffect( () => {
    const newLayout = layouts.find( ( layout ) => layout.id === selectedLayout );
    setLayout( newLayout );
  }, [ selectedLayout, layouts ] );

  useEffect( () => {
    if ( orientation ) {
      handleCanvasInitialization( scale, orientation );
    }

    if ( fabricCanvasInstanceRef.current ) {
      handleTextAlignment( fabricCanvasInstanceRef.current, qrCode, scale );
    }
  }, [ scale, qrCode, orientation ] );

  useEffect(() => {
    if (layout && fabricCanvasInstanceRef.current && scaledWidth > 0 && scaledHeight > 0) {

      initializeCanvasChild( layout );

      if ( signatures ) {

        const existingObjects = fabricCanvasInstanceRef.current?.getObjects() || [];

        for ( const signature of signatures ) {
          const layouts = Array.isArray( signature.layout )
                          ? signature.layout
                          : [ signature.layout ];

          if (
            signature.base64 &&
            layouts.includes( selectedLayout )
          ) {
            const isAlreadyDrawn = existingObjects.some(
              ( obj: any ) => obj.base64 === signature.base64
            );

            if ( !isAlreadyDrawn ) {
              try {
                createCanvasSignature( signature.base64,
                  signature.color!,
                  fabricCanvasInstanceRef.current,
                  dispatch,
                  selectedLayout,
                  signature );
              } catch ( error ) {
                console.error( "Error creating signature :", error );
              }
            }
          }
        }
      }
      fabricCanvasInstanceRef.current.requestRenderAll();
      }
    }, [scale, scaledWidth, scaledHeight, signatures.length, layout, existingData])

  useEffect( () => {
    initializeCanvasWithLayout(
      layout,
      fabricCanvasInstanceRef,
      scaledWidth,
      scaledHeight,
      initializeCanvasChild
    );
  }, [ layout, scaledWidth, scaledHeight ] );

  /**
   * Handles the initialization of a canvas with the specified scale and orientation.
   * It computes the scaled dimensions based on the given scale and orientation,
   * and initializes or updates the canvas as needed.
   *
   * @param {number} scale - The scaling factor to adjust the canvas size.
   * @param {string} orientation - The orientation to determine the base dimensions (e.g., 'landscape', 'portrait').
   * @return {void} This method does not return a value.
   */
  function handleCanvasInitialization( scale: number, orientation: string ): void {
    const parsedWidth = COMPLETE_SIZE_BASE[ orientation ].width * scale;
    const parsedHeight = COMPLETE_SIZE_BASE[ orientation ].height * scale;

    setScaledWidth( parsedWidth );
    setScaledHeight( parsedHeight );

    if ( !fabricCanvasInstanceRef.current ) {
      initializeCanvas( parsedWidth, parsedHeight );
    }
  }

  /**
   * Adjusts the alignment of text objects on the given canvas, considering QR code presence and scaling.
   *
   * @param {fabric.Canvas} canvas - The canvas object containing elements to be aligned.
   * @param {boolean} qrCode - Indicates whether a QR code is present, which affects the text alignment.
   * @param {number} scale - The scaling factor applied to the canvas and its elements.
   * @return {void} This function does not return any value; it modifies the canvas elements directly and re-renders
   *   the canvas.
   */
  function handleTextAlignment( canvas: fabric.Canvas, qrCode: boolean, scale: number ): void {
    const qrCodeHeight = 150 * scale;
    const objects: CustomFabricObject[] = canvas.getObjects();

    objects.forEach( obj => {
      if ( obj.type === "textbox" ) {
        const initialTop = parseFloat( obj.initialTop || "0" );
        const canvasHeight = obj.maxHeight || scaledHeight;

        let newCanvasHeight = isQrCodeEnabled && qrCode ? canvasHeight - qrCodeHeight : canvasHeight;

        switch ( obj.originY ) {
          case "center":
            obj.set( { top: initialTop + newCanvasHeight / 2 } );
            break;
          case "bottom":
            obj.set( { top: initialTop + newCanvasHeight } );
            break;
          case "top":
            obj.set( { top: initialTop } );
            break;
          default:
            break;
        }
        obj.setCoords();
      }
    } );

    canvas.renderAll();
  }

  /**
   * Initializes the canvas element with the specified dimensions and sets up event listeners for user interactions.
   *
   * This function is responsible for the following tasks:
   * - Checks if the canvas element reference and the provided dimensions are valid, and returns if not.
   * - Clears the canvas if there is an existing instance of the fabric.js canvas.
   * - Creates a new fabric.js canvas instance with the provided dimensions and attaches it to the canvas element.
   * - Adds event listeners to the canvas for handling user interactions, such as clearing the active object on click
   * outside the canvas and displaying a border around text boxes on mouse over.
   *
   * @param scaledWidth - The scaled width of the canvas.
   * @param scaledHeight - The scaled height of the canvas.
   */
  const initializeCanvas = async (
    scaledWidth: number,
    scaledHeight: number
  ) => {

    if ( !canvasElementRef.current || scaledWidth === 0 || scaledHeight === 0 ) return;

    if ( fabricCanvasInstanceRef.current ) clearCanvas( fabricCanvasInstanceRef.current );

    if ( !fabricCanvasInstanceRef.current ) {
      fabricCanvasInstanceRef.current = new fabric.Canvas(
        canvasElementRef.current,
        {
          width: scaledWidth,
          height: scaledHeight,
          selection: false,
          allowTouchScrolling: true,
        }
      );

      const canvas = fabricCanvasInstanceRef.current;

      window.getFabricCanvas = () => fabricCanvasInstanceRef.current;

      const state = {
        isPinching: false,
        startPinchDistance: 0,
        isDragging: false,
        dragStartedInsideImage: false,
        lastPosition: { x: 0, y: 0 },
        initialTop: 0,
        initialLeft: 0,
      };

      if ( fabricCanvasInstanceRef.current && fabricCanvasInstanceRef.current.upperCanvasEl ) {

        fabricCanvasInstanceRef.current.upperCanvasEl.removeEventListener(
          "touchstart",
          fabricCanvasInstanceRef.current._onTouchStart
        );

        fabricCanvasInstanceRef.current.upperCanvasEl.addEventListener(
          "touchstart",
          ( event: TouchEvent ) => {
            const touch = event.touches[ 0 ];
            const target = fabricCanvasInstanceRef.current.findTarget( touch );

            if ( !target ) return;

            event.preventDefault();
            fabricCanvasInstanceRef.current._onTouchStart( event );
          }
        );

        canvas.on( "object:moving", ( event ) => {
          const target = event.target;

          if ( !target || !target._originalElement ) return;
          if ( target._originalElement.currentSrc.includes( "83a41560d09c6477f0e293027ce" ) ) {
            target.top = state.initialTop;
            target.left = state.initialLeft;

            target.setCoords();
            canvas.renderAll();
          }
        } );

        // Mobile
        canvas.upperCanvasEl.addEventListener( "touchstart", handleTouchStart( canvas, state ) );
        canvas.upperCanvasEl.addEventListener( "touchmove", handleTouchMove( canvas, state ) );
        canvas.upperCanvasEl.addEventListener( "touchend", handleTouchEnd( canvas, state ) );

        canvas.on( "mouse:move", ( event ) => {
          if ( state.isDragging && canvas.getZoom() > 1 ) {
            const deltaX = event.e.clientX - state.lastPosition.x;
            const deltaY = event.e.clientY - state.lastPosition.y;

            state.lastPosition = { x: event.e.clientX, y: event.e.clientY };

            const viewPort = canvas.viewportTransform!;
            viewPort[ 4 ] += deltaX;
            viewPort[ 5 ] += deltaY;
            canvas.setViewportTransform( viewPort );

            checkCanvasBounds( canvas );
          }
        } );
        canvas.on( "mouse:up", () => {
          state.isDragging = false;
        } );

        // hover
        canvas.on( "mouse:over", handleMouseOver( canvas ) );
        canvas.on( "mouse:out", handleMouseOut( canvas ) );

      canvas.on('selection:cleared', () => {
          handleDisplayToolbar( dispatch, "layout" );
        } );
      }
      const originalFindTarget = fabricCanvasInstanceRef.current.findTarget;

      fabricCanvasInstanceRef.current.findTarget = function ( e, skipGroup ) {
        const target = originalFindTarget.call( this, e, skipGroup );

        if ( target && target.type === "image" ) {
          const pointer = this.getPointer( e );
          const isInsideClipRect = checkIfInsideClipRect( pointer, target.clipPath );
          if ( !isInsideClipRect ) {
            return null;
          }
        }
        return target;
      };
    }
  };



  /**
   * Initializes the canvas with a new layout, or restores an existing layout.
   * This function is responsible for creating and managing the canvas elements,
   * including images and text boxes, based on the provided layout data.
   *
   * @param {LayoutElement} newLayout - The new layout to be initialized on the canvas.
   * @returns {void}
   */
  const initializeCanvasChild = async ( newLayout: LayoutElement ) => {
    let radius;
    try {
      if ( !fabricCanvasInstanceRef.current || fabricCanvasInstanceRef.current.width === 0 || fabricCanvasInstanceRef.current.height === 0 ) return;

        if( scaledDimensions.width === scaledWidth && scaledDimensions.height === scaledHeight && scaledDimensions.layout === selectedLayout ) return;
      clearCanvas( fabricCanvasInstanceRef.current );

      fabricCanvasInstanceRef.current.setDimensions( {
        width: scaledWidth,
        height: scaledHeight
      } );

      setScaledDimensions( { width: scaledWidth, height: scaledHeight, layout: selectedLayout } );

      const existingLayout = currentData.layoutData.find(
        ( layout ) => layout.id === newLayout.id
      );

      let textDatasCounter = 0;
      dispatch( {
        type: "editor/updateIsLoading",
        payload: true,
      } );
      if ( !existingLayout || existingLayout.id !== selectedLayout ) {

        let textIndexCounter = 0;
        for ( const [ index, item ] of newLayout.elements.entries() ) {

          if ( item.type === "img" ) {
            radius = parseFloat( item.style[ "border-radius" ] ) || 0;

            let fabricImage;
            const premadeItem = existingData?.data?.img?.find( ( data ) => data.index === index );

            if ( premadeItem ) {
              fabricImage = await recreateImage(item, scale, radius, fabricCanvasInstanceRef, dispatch, selectedLayout, index, premadeItem);
            } else {
              fabricImage = await createCanvasImage( placeholderBase64,
                item,
                scale,
                radius,
                fabricCanvasInstanceRef,
                dispatch,
                selectedLayout,
                index );
              fabricImage.on( "mousedown", ( event ) => {
                event.e.stopPropagation();
                handleDisplayToolbar( dispatch, "image", index );
              } );
            }
          } else if ( item.type === "mixedContent" ) {
            let textbox;
            if ( textDatas.length > 0 ) {
              if ( textDatas[ textDatasCounter ].index === index ) {
                const premadeItem = textDatas[ textDatasCounter ];
                textbox = await createCanvasTextBox( item,
                  scale,
                  fabricCanvasInstanceRef,
                  dispatch,
                  selectedLayout,
                  index,
                  qrCode,
                  premadeItem );
                textDatasCounter++;
              } else {
                const premadeItem = textDatas[ textDatasCounter ];
                textbox = await createCanvasTextBox( item,
                  scale,
                  fabricCanvasInstanceRef,
                  dispatch,
                  selectedLayout,
                  index,
                  qrCode,
                  premadeItem );
                textDatasCounter++;
              }
            } else {
              const premadeItemIndex = existingData && existingData.data && existingData.data.dataTxtCard && existingData.data.dataTxtCard.findIndex(
                ( _, index ) => index === textIndexCounter );
              const premadeItem = premadeItemIndex && premadeItemIndex !== -1 ?
                                  existingData && existingData.data.dataTxtCard[ premadeItemIndex ] :
                                  null;
              textbox = await createCanvasTextBox( item,
                scale,
                fabricCanvasInstanceRef,
                dispatch,
                selectedLayout,
                index,
                qrCode,
                premadeItem );
            }

            textbox.on( "mousedown", ( event ) => {
              event.e.stopPropagation();
              handleDisplayToolbar( dispatch, "text", textIndexCounter );
            } );
            textIndexCounter++;
          }
        }
        ;

        dispatch( {
          type: "editor/addCurrentDataLayoutData",
          payload: {
            id: newLayout.id,
            disabled: newLayout.disabled ? newLayout.disabled : [],
            enabled: newLayout.enabled ? newLayout.enabled : [],
            borderRadius: radius,
            fabricElements: JSON.stringify( fabricCanvasInstanceRef.current.toJSON() )
          }
        } );
        fabricCanvasInstanceRef.current.renderAll();

      } else {
        let fabricData = JSON.parse( existingLayout.fabricElements ).objects;
        fabricData = fabricData.filter( elem => elem.type === "Image" || elem.type === "Textbox" );

        let textIndexCounter = 0;

        for ( const [ index, item ] of newLayout.elements.entries() ) {
          if ( item.type === "img" ) {
            radius = parseFloat( item.style[ "border-radius" ] ) || 0;

              const element = fabricData.find( elem => elem.idx === index );
              const fabricImage = await createCanvasImageExisting( item,
                scale,
                radius,
                fabricCanvasInstanceRef,
                element,
                dispatch,
                selectedLayout,
                index );


            fabricImage.on( "mousedown", ( event ) => {
              event.e.stopPropagation();
              handleDisplayToolbar( dispatch, "image", index );
            } );
          } else if ( item.type === "mixedContent" ) {
            let textbox;

            if ( textDatas[ textDatasCounter ].index === index ) {
              const premadeItem = textDatas[ textDatasCounter ];
              textbox = await createCanvasTextBoxExisting( item,
                scale,
                fabricCanvasInstanceRef,
                dispatch,
                selectedLayout,
                fabricData[ index ],
                index,
                qrCode,
                premadeItem );
              textDatasCounter++;
            } else {
              const premadeItem = textDatas[ textDatasCounter ];
              textbox = await createCanvasTextBoxExisting( item,
                scale,
                fabricCanvasInstanceRef,
                dispatch,
                selectedLayout,
                fabricData[ index ],
                index,
                qrCode,
                premadeItem );
              textDatasCounter++;
            }

            // const textbox = await createCanvasTextBoxExisting(item, scale, fabricCanvasInstanceRef, dispatch,
            // selectedLayout, fabricData[index], index, qrCode, premadeItem);

            const getAlert = () => store.getState().editor.alert;
            const alerts = getAlert();

            const activeAlert = alerts.filter( alert => alert.layout === selectedLayout && alert.index === textbox.textIndexCounter );

            if ( activeAlert.length > 0 ) {
              textbox.alertRect = createAlertRect( textbox );
              fabricCanvasInstanceRef.current.add( textbox.alertRect );
            }

            textbox.on( "mousedown", ( event ) => {
              event.e.stopPropagation();
              handleDisplayToolbar( dispatch, "text", textIndexCounter );
            } );
            textIndexCounter++;
          }
        }
        ;

        fabricCanvasInstanceRef.current.renderAll();
        dispatch( {
          type: "editor/addCurrentDataLayoutData",
          payload: {
            id: existingLayout.id,
            disabled: existingLayout.disabled ? existingLayout.disabled : [],
            enabled: existingLayout.enabled ? existingLayout.enabled : [],
            borderRadius: radius,
            fabricElements: JSON.stringify( fabricCanvasInstanceRef.current.toJSON() )
          }
        } );
      }
      dispatch( {
        type: "editor/updateIsLoading",
        payload: false,
      } );

    } catch ( error ) {
      console.log( error );
    } finally {
      removeDuplicateObjects( fabricCanvasInstanceRef.current );
      const placeHolderObjects = fabricCanvasInstanceRef.current && fabricCanvasInstanceRef.current._objects.some( ( object ) => object.type == "image" && object.placeholderImage);
      dispatch( {
        type: "editor/updateIsExportable",
        payload: !placeHolderObjects
      } );
    }
  };

  return (
    <>
      <div className="w-100 h-100 d-flex justify-content-center">
        { fabricCanvasInstanceRef.current &&
          toolbarDisplay &&
          renderModal(
            type,
            fabricCanvasInstanceRef.current,
            toolbarDisplay.index
          ) }
        { orientation && (
          <div
            id={ "capture" }
          >
            { alert.map( ( singleAlert ) => {
              if ( !singleAlert.show || singleAlert.layout !== selectedLayout ) return null;
              return (
                <div
                  key={ singleAlert.index }
                  // variant={ singleAlert.type }
                  className={ `alert alert-${ singleAlert.type } position-absolute top-0 start-50 translate-middle-x` }
                  style={ {
                    zIndex: 3000
                  } }
                >
                  { singleAlert.message }
                </div>
              );
            } ) }
            <div
              id="template-container"
              data-test={ "template-container" }
              className="position-relative mw-100 mh-100"
              style={ {
                width: `${ COMPLETE_SIZE_BASE[ orientation ].width * scale }px`,
                height: `${ COMPLETE_SIZE_BASE[ orientation ].height * scale }px`,
                aspectRatio: COMPLETE_SIZE_BASE[ orientation ].aspectRatio,
                margin: "0 auto"
              } }
            >
              {/* Image de fond */ }
              <div
                id="content-container"
                style={ {
                  position: "relative",
                  maxWidth: "100%",
                  maxHeight: "100%",
                  width: "100%",
                  height: scaledHeight,
                  zIndex: 2
                } }
              >
                <canvas
                  ref={ canvasElementRef }
                  data-test="fabricCanvas"
                  style={ {
                    touchAction: "manipulation",
                    WebkitOverflowScrolling: "touch"
                  } }
                />
                { qrCode && isQrCodeEnabled && (
                  <div className="position-absolute bottom-0 start-0 bg-white d-flex align-items-center"
                       style={ {
                         height: `${ 150 * scale }px`,
                         padding: `${ 1 * scale }rem`,
                         gap: `${ 1 * scale }rem`,
                         zIndex: 1,
                         width: `${ COMPLETE_SIZE_BASE[ orientation ].width }px`
                       } }>
                    <div className="postition-relative d-flex justify-content-center align-items-center flex-column me-2"
                         style={ { maxHeight: "100%", height: "100%" } }>
                      <img src={ `${ process.env.PREFIX_URL }/v2017/img/qrcode-demo-bloc-reponse.svg` }
                           style={ { objectFit: "contain", height: "80%" } } />
                      <p className="m-0 small-text">scan</p>
                    </div>
                    <div>
                      <p className="m-0 small-text">Pour me répondre :</p>
                      <p className="small-text">{ process.env.PREFIX_URL }/re/ty8</p>
                    </div>
                  </div>
                ) }
              </div>
            </div>
          </div>
        ) }
      </div>
    </>
  );
}